import { useEffect, useMemo, useState } from 'react';

import * as monthUtils from 'loot-core/shared/months';
import type { IntegerAmount } from 'loot-core/shared/util';
import type {
  CategoryEntity,
  CategoryGroupEntity,
} from 'loot-core/types/models';

import { useCategories } from './useCategories';
import { useSpreadsheet } from './useSpreadsheet';
import { useSyncedPref } from './useSyncedPref';

import {
  envelopeBudget,
  trackingBudget,
} from '@desktop-client/spreadsheet/bindings';

type CategoryBudgetData = {
  category: CategoryEntity;
  budgeted: IntegerAmount;
  spent: IntegerAmount;
  balance: IntegerAmount;
};

type GroupBudgetData = {
  group: CategoryGroupEntity;
  categories: CategoryBudgetData[];
  totalBudgeted: IntegerAmount;
  totalSpent: IntegerAmount;
};

type BudgetMonthData = {
  groups: GroupBudgetData[];
  totalBudgeted: IntegerAmount;
  totalSpent: IntegerAmount;
  totalBalance: IntegerAmount;
  isLoading: boolean;
};

export function useBudgetMonthData(month: string): BudgetMonthData {
  const spreadsheet = useSpreadsheet();
  const [budgetType = 'envelope'] = useSyncedPref('budgetType');

  const {
    data: { list: categories, grouped: categoryGroups } = {
      list: [],
      grouped: [],
    },
  } = useCategories();

  const [budgetedByCategory, setBudgetedByCategory] = useState<
    Record<string, IntegerAmount>
  >({});
  const [spentByCategory, setSpentByCategory] = useState<
    Record<string, IntegerAmount>
  >({});
  const [balanceByCategory, setBalanceByCategory] = useState<
    Record<string, IntegerAmount>
  >({});

  const sheetName = monthUtils.sheetForMonth(month);

  const expenseCategories = useMemo(
    () => categories.filter(c => !c.is_income && !c.tombstone && !c.hidden),
    [categories],
  );

  const expenseGroups = useMemo(
    () => categoryGroups.filter(g => !g.is_income && !g.tombstone && !g.hidden),
    [categoryGroups],
  );

  // Bind to budgeted amounts
  useEffect(() => {
    const unbindList: (() => void)[] = [];
    for (const category of expenseCategories) {
      const binding =
        budgetType === 'tracking'
          ? trackingBudget.catBudgeted(category.id)
          : envelopeBudget.catBudgeted(category.id);

      const unbind = spreadsheet.bind(sheetName, binding, result => {
        setBudgetedByCategory(prev => ({
          ...prev,
          [category.id]: (result.value as number) || 0,
        }));
      });
      unbindList.push(unbind);
    }
    return () => unbindList.forEach(u => u());
  }, [budgetType, expenseCategories, sheetName, spreadsheet]);

  // Bind to spent amounts
  useEffect(() => {
    const unbindList: (() => void)[] = [];
    for (const category of expenseCategories) {
      const binding =
        budgetType === 'tracking'
          ? trackingBudget.catSumAmount(category.id)
          : envelopeBudget.catSumAmount(category.id);

      const unbind = spreadsheet.bind(sheetName, binding, result => {
        setSpentByCategory(prev => ({
          ...prev,
          [category.id]: (result.value as number) || 0,
        }));
      });
      unbindList.push(unbind);
    }
    return () => unbindList.forEach(u => u());
  }, [budgetType, expenseCategories, sheetName, spreadsheet]);

  // Bind to balance amounts
  useEffect(() => {
    const unbindList: (() => void)[] = [];
    for (const category of expenseCategories) {
      const binding =
        budgetType === 'tracking'
          ? trackingBudget.catBalance(category.id)
          : envelopeBudget.catBalance(category.id);

      const unbind = spreadsheet.bind(sheetName, binding, result => {
        setBalanceByCategory(prev => ({
          ...prev,
          [category.id]: (result.value as number) || 0,
        }));
      });
      unbindList.push(unbind);
    }
    return () => unbindList.forEach(u => u());
  }, [budgetType, expenseCategories, sheetName, spreadsheet]);

  // Reset state when month changes
  useEffect(() => {
    setBudgetedByCategory({});
    setSpentByCategory({});
    setBalanceByCategory({});
  }, [month]);

  const isLoading =
    expenseCategories.length > 0 &&
    Object.keys(budgetedByCategory).length === 0 &&
    Object.keys(spentByCategory).length === 0;

  return useMemo(() => {
    const groups: GroupBudgetData[] = expenseGroups
      .map(group => {
        const groupCategories = (group.categories || [])
          .filter(c => !c.tombstone && !c.hidden)
          .map(cat => ({
            category: cat,
            budgeted: budgetedByCategory[cat.id] || 0,
            spent: spentByCategory[cat.id] || 0,
            balance: balanceByCategory[cat.id] || 0,
          }));

        const totalBudgeted = groupCategories.reduce(
          (sum, c) => sum + c.budgeted,
          0,
        );
        const totalSpent = groupCategories.reduce((sum, c) => sum + c.spent, 0);

        return {
          group,
          categories: groupCategories,
          totalBudgeted,
          totalSpent,
        };
      })
      .filter(g => g.categories.length > 0);

    const totalBudgeted = groups.reduce((sum, g) => sum + g.totalBudgeted, 0);
    const totalSpent = groups.reduce((sum, g) => sum + g.totalSpent, 0);
    const totalBalance = totalBudgeted + totalSpent;

    return {
      groups,
      totalBudgeted,
      totalSpent,
      totalBalance,
      isLoading,
    };
  }, [
    balanceByCategory,
    budgetedByCategory,
    expenseGroups,
    isLoading,
    spentByCategory,
  ]);
}
